import './index.scss';

import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from 'react';
import { useTranslation } from 'react-i18next';

export type CButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'destructive'
  | 'danger2'
  | 'info'
  | 'link'
  | 'success';
export type CButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'pro';
export type CButtonTone = 'default' | 'danger';
export type CButtonRef = HTMLButtonElement | HTMLAnchorElement;

type CommonProps = {
  variant?: CButtonVariant;
  size?: CButtonSize;
  tone?: CButtonTone;
  loading?: boolean;
  loadingLabel?: string;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  block?: boolean;
};

type TextButtonProps = {
  iconOnly?: false;
  children: ReactNode;
};

type IconButtonProps = {
  iconOnly: true;
  startIcon: ReactNode;
  children?: never;
  'aria-label': string;
};

type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  keyof CommonProps | 'children'
> & {
  href?: never;
};

type AnchorButtonProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof CommonProps | 'children' | 'href'
> & {
  href: string;
  disabled?: boolean;
};

export type CButtonProps = CommonProps &
  (TextButtonProps | IconButtonProps) &
  (NativeButtonProps | AnchorButtonProps);

const getClassName = ({
  variant,
  size,
  tone,
  block,
  iconOnly,
  loading,
  className,
}: Required<Pick<CommonProps, 'variant' | 'size' | 'tone'>> &
  Pick<CommonProps, 'block' | 'loading'> & {
    iconOnly?: boolean;
    className?: string;
  }) =>
  [
    'c-button',
    `c-button--${variant}`,
    `c-button--${size}`,
    tone === 'danger' && 'c-button--danger',
    block && 'c-button--block',
    iconOnly && 'c-button--icon-only',
    loading && 'c-button--loading',
    className,
  ]
    .filter(Boolean)
    .join(' ');

export const CButton = forwardRef<CButtonRef, CButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      tone = 'default',
      loading = false,
      loadingLabel,
      startIcon,
      endIcon,
      block = false,
      iconOnly = false,
      className,
      children,
      ...elementProps
    },
    ref
  ) => {
    const { t } = useTranslation();
    const classes = getClassName({ variant, size, tone, block, iconOnly, loading, className });
    const content = (
      <>
        {loading ? (
          <span aria-hidden="true" className="c-button__spinner" />
        ) : startIcon ? (
          <span className="c-button__icon">{startIcon}</span>
        ) : null}
        {!iconOnly ? <span className="c-button__label">{children}</span> : null}
        {!loading && endIcon ? <span className="c-button__icon">{endIcon}</span> : null}
        {loading ? (
          <span className="c-button__sr-only">{loadingLabel ?? t('common:common.loading')}</span>
        ) : null}
      </>
    );

    if ('href' in elementProps && typeof elementProps.href === 'string') {
      const {
        href,
        disabled = false,
        onClick,
        rel,
        target,
        title,
        ...anchorProps
      } = elementProps as AnchorButtonProps;
      const handleClick: MouseEventHandler<HTMLAnchorElement> = (event) => {
        if (disabled || loading) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onClick?.(event);
      };

      return (
        <a
          {...anchorProps}
          ref={ref as Ref<HTMLAnchorElement>}
          href={disabled ? undefined : href}
          target={target}
          rel={target === '_blank' ? (rel ?? 'noopener noreferrer') : rel}
          title={title ?? (iconOnly ? anchorProps['aria-label'] : undefined)}
          aria-busy={loading || undefined}
          aria-disabled={disabled || loading || undefined}
          className={classes}
          onClick={handleClick}
        >
          {content}
        </a>
      );
    }

    const {
      disabled = false,
      onClick,
      title,
      type = 'button',
      ...buttonProps
    } = elementProps as NativeButtonProps;
    const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
      if (loading) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      onClick?.(event);
    };

    return (
      <button
        {...buttonProps}
        ref={ref as Ref<HTMLButtonElement>}
        type={type === 'submit' ? 'submit' : type === 'reset' ? 'reset' : 'button'}
        disabled={disabled}
        title={title ?? (iconOnly ? buttonProps['aria-label'] : undefined)}
        aria-busy={loading || undefined}
        aria-disabled={loading || undefined}
        className={classes}
        onClick={handleClick}
      >
        {content}
      </button>
    );
  }
);
